/**
 * 点阵发光背景（P1 C-2a）——单张 2D canvas 的鼠标近邻发光网格，纯装饰层。
 *
 * 这是通用技术（网格点 + 鼠标距离提亮）的**我方原创实现**，不照搬任何竞品画面。
 * 性能三条硬约束（否则会拖累后期视频生成）：
 *  1. 单 canvas 绘制，绝不生成成千上万个 DOM 节点；
 *  2. 点数按容器面积与 spacing 封顶；切后台（visibilitychange）暂停 rAF，不占后台 CPU；
 *  3. 每帧不新建字符串/对象——预生成 alpha 调色板，复用坐标。
 * 另：prefers-reduced-motion 时降级为静态一帧（不跑动画）。
 */
import { useEffect, useRef } from 'react';

export interface DotMatrixBackgroundProps {
  /** 点间距（px），越大点越稀越省 */
  spacing?: number;
  /** 鼠标影响半径（px） */
  radius?: number;
  className?: string;
}

/** 预生成的蓝紫色 alpha 档位表，避免每帧拼字符串 */
const ALPHA_STEPS = 24;
function buildPalette(): string[] {
  return Array.from(
    { length: ALPHA_STEPS + 1 },
    (_, i) => `rgba(124,132,255,${((i / ALPHA_STEPS) * 0.8).toFixed(3)})`,
  );
}

export function DotMatrixBackground({
  spacing = 20,
  radius = 150,
  className = '',
}: DotMatrixBackgroundProps) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (canvas === null) {
      return;
    }
    const context2d = canvas.getContext('2d');
    if (context2d === null) {
      return;
    }
    // 捕获为非空局部，供下方嵌套函数闭包使用（否则 TS 在函数内丢失窄化）
    const cv = canvas;
    const ctx = context2d;

    const palette = buildPalette();
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let w = 0;
    let h = 0;
    let cols = 0;
    let rows = 0;
    let raf = 0;
    let running = false;
    const mouse = { x: -99999, y: -99999 };
    let rect = cv.getBoundingClientRect();

    function resize(): void {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      rect = cv.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      cv.width = Math.max(1, Math.floor(w * dpr));
      cv.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(w / spacing);
      rows = Math.ceil(h / spacing);
    }

    function draw(): void {
      ctx.clearRect(0, 0, w, h);
      const r2 = radius * radius;
      for (let i = 0; i <= cols; i += 1) {
        const x = i * spacing;
        for (let j = 0; j <= rows; j += 1) {
          const y = j * spacing;
          const dx = x - mouse.x;
          const dy = y - mouse.y;
          const d2 = dx * dx + dy * dy;
          let size = 0.8;
          let level = 4; // 基础微光档
          if (d2 < r2) {
            const t = 1 - d2 / r2; // 0..1，越近越亮
            size = 0.8 + t * 1.7;
            level = Math.min(ALPHA_STEPS, 4 + Math.round(t * (ALPHA_STEPS - 4)));
          }
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fillStyle = palette[level] ?? palette[4] ?? '';
          ctx.fill();
        }
      }
    }

    function loop(): void {
      draw();
      raf = requestAnimationFrame(loop);
    }
    function start(): void {
      if (!running && !reduce) {
        running = true;
        raf = requestAnimationFrame(loop);
      }
    }
    function stop(): void {
      running = false;
      cancelAnimationFrame(raf);
    }

    function onMove(e: MouseEvent): void {
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    }
    function onLeave(): void {
      mouse.x = -99999;
      mouse.y = -99999;
    }
    function onVisibility(): void {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    }
    function onScroll(): void {
      rect = cv.getBoundingClientRect();
    }

    resize();
    if (reduce) {
      draw(); // 静态一帧，不跑动画
    } else {
      start();
    }

    window.addEventListener('resize', resize);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseout', onLeave);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseout', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [spacing, radius]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
}
